import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ThreeDImage from "@/components/ThreeDImage";
import { OCCASIONS, HEADLINE_OCCASIONS, IMG, aiOr, api } from "@/lib/api";
import { toast } from "sonner";
import {
  CaretRight,
  Sparkle,
  FilmSlate,
  MusicNotes,
  Confetti,
  Heart,
  ShieldStar,
  Cake,
  Star,
  Popcorn,
  Cake as CakeIcon,
  HeartStraight,
  Diamond,
  UsersFour,
  Briefcase,
  Crown,
  MonitorPlay,
  ShieldCheck,
  MagicWand,
  FilmSlate as Clapperboard,
} from "@phosphor-icons/react";

const HERO = aiOr("hero_theater", IMG.hero);
const SEATS = aiOr("gallery_seats", IMG.theaterSeats);
const SCREEN = aiOr("gallery_screen", IMG.hero);

const FEATURES = [
  { icon: FilmSlate, title: "Giant 4K Screen", body: "A cinema-grade curved screen just for your crew." },
  { icon: MusicNotes, title: "Dolby-Immersive Sound", body: "Feel every beat, whisper and cheer." },
  { icon: Confetti, title: "Custom Decorations", body: "Balloons, LEDs, candles — themed to your moment." },
  { icon: Heart, title: "Fully Private", body: "The entire hall is yours. No strangers. No sharing." },
  { icon: ShieldStar, title: "OTP-Verified Bookings", body: "Secure, guaranteed slot with a verified number." },
  { icon: Cake, title: "Cake & Add-ons", body: "Cake, snacks, photography, video montage — sorted." },
];

const OCCASION_ICONS = {
  birthday: CakeIcon,
  proposal: HeartStraight,
  anniversary: Diamond,
  friends: UsersFour,
  corporate: Briefcase,
  family: HeartStraight,
  surprise: MagicWand,
};

const FEATURES_BAR = [
  { icon: Crown, label: "Premium Experience" },
  { icon: MonitorPlay, label: "State of the Art Tech" },
  { icon: ShieldCheck, label: "Complete Privacy" },
  { icon: MagicWand, label: "Customized Setup" },
];

const TESTIMONIALS = [
  {
    name: "Ananya & Rohan",
    occasion: "Marriage Proposal",
    quote:
      "The screen lit up with our story, then the words 'Will you marry me?' — I couldn't have imagined a more cinematic yes.",
  },
  {
    name: "The Kapoor Family",
    occasion: "Family Gathering",
    quote:
      "Three generations. One screen. Endless laughter. MoonGlow made grandma's 75th feel like a movie premiere.",
  },
  {
    name: "Aditi",
    occasion: "Birthday",
    quote:
      "Every candle, every balloon, every song — perfect. My friends still text me about the private theater.",
  },
];

const STATS = [
  { k: "1,200+", v: "Celebrations" },
  { k: "4.9★", v: "Average Rating" },
  { k: "2-6 hrs", v: "Flexible Slots" },
  { k: "100%", v: "Private Hall" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [contact, setContact] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [packages, setPackages] = useState([]);
  const [food, setFood] = useState([]);
  const [beverages, setBeverages] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/food-items").then((r) => setFood(r.data)).catch(() => {});
    api.get("/beverage-items").then((r) => setBeverages(r.data)).catch(() => {});
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.message.trim()) {
      toast.error("Please share your name and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", contact);
      toast.success("Message received. We will call you back shortly.");
      setContact({ name: "", phone: "", email: "", message: "" });
    } catch (e) {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-mg text-white">
      <Header />

      {/* HERO */}
      <section data-testid="hero" className="relative pt-20 grain overflow-hidden">
        <div className="absolute inset-0">
          <ThreeDImage src={HERO} alt="MoonGlow private theater" className="w-full h-full object-cover object-center opacity-70" />
          <div className="absolute inset-0 hero-overlay" />
          {/* subtle twinkle stars */}
          {Array.from({ length: 40 }).map((_, i) => (
            <div
              key={i}
              className="twinkle absolute bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                animationDelay: `${Math.random() * 4}s`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 md:pt-32 md:pb-44">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl"
          >
            <div className="label-tiny flex items-center gap-3">
              <span className="h-px w-8 bg-gold" style={{ backgroundColor: "var(--mg-gold)" }} />
              Private · Luxurious · Yours Alone
            </div>
            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              <span className="silver-gradient-text">Your moments,</span>
              <br />
              <span className="gold-gradient-text">on the giant screen.</span>
            </h1>
            <p className="mt-8 max-w-xl text-white/80 text-base sm:text-lg leading-relaxed">
              MoonGlow Screens is a premium private theater where birthdays turn cinematic, proposals become
              premieres, and every celebration writes its own credits.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                data-testid="hero-book-btn"
                onClick={() => navigate("/book")}
                className="btn-gold rounded-md px-7 py-3.5 text-sm inline-flex items-center gap-2"
              >
                Book Your Screening <CaretRight size={16} weight="bold" />
              </button>
              <a
                href="#occasions"
                data-testid="hero-explore-btn"
                className="btn-ghost-gold rounded-md px-7 py-3.5 text-sm inline-flex items-center gap-2"
              >
                Explore Occasions
              </a>
            </div>

            {/* Stats strip */}
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl">
              {STATS.map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl gold-gradient-text">{s.k}</div>
                  <div className="label-tiny mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* marquee tagline */}
        <div className="relative border-t border-mg bg-black/60 backdrop-blur-md">
          <div className="marquee-inner flex whitespace-nowrap py-4 gap-16">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-16 label-tiny">
                <span className="text-gold">Your Moments</span>
                <Star size={12} weight="fill" className="text-gold" />
                <span className="silver-gradient-text">Our Screen</span>
                <Star size={12} weight="fill" className="text-gold" />
                <span className="text-gold">Timeless Memories</span>
                <Star size={12} weight="fill" className="text-gold" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OCCASIONS */}
      <section id="occasions" data-testid="occasions" className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Icon strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
            {HEADLINE_OCCASIONS.map((key) => {
              const o = OCCASIONS.find((x) => x.key === key);
              const Icon = OCCASION_ICONS[key] || Sparkle;
              return (
                <button
                  key={key}
                  data-testid={`occasion-icon-${key}`}
                  onClick={() => navigate(`/book?occasion=${key}`)}
                  className="card-lux rounded-lg p-5 lg:p-6 flex flex-col items-center text-center group"
                >
                  <div className="w-14 h-14 rounded-full flex items-center justify-center border" style={{ borderColor: "var(--mg-gold-border)" }}>
                    <Icon size={24} weight="duotone" className="text-gold" />
                  </div>
                  <div className="label-tiny text-gold mt-4">{o.label.toUpperCase()}</div>
                  <p className="mt-2 text-[11px] text-white/60 leading-relaxed max-w-[16ch]">{o.short}</p>
                </button>
              );
            })}
          </div>

          {/* Section title */}
          <div className="mt-16 flex items-center gap-4 justify-center text-center">
            <div className="hidden sm:block h-px w-14" style={{ background: "linear-gradient(90deg, transparent, var(--mg-gold))" }} />
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl">
              <span className="silver-gradient-text">Private Theater</span>{" "}
              <span className="gold-gradient-text">for every occasion</span>
            </h2>
            <div className="hidden sm:block h-px w-14" style={{ background: "linear-gradient(90deg, var(--mg-gold), transparent)" }} />
          </div>

          {/* Card grid — 5 headline occasions */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6 mt-12">
            {HEADLINE_OCCASIONS.map((key, i) => {
              const o = OCCASIONS.find((x) => x.key === key);
              return (
                <motion.button
                  key={key}
                  data-testid={`occasion-card-${key}`}
                  onClick={() => navigate(`/book?occasion=${key}`)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.06, duration: 0.5 }}
                  className="card-lux rounded-lg overflow-hidden group text-left"
                >
                  <div className="aspect-[4/5] relative overflow-hidden">
                    <ThreeDImage
                      src={o.img}
                      alt={o.label}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  </div>
                  <div className="px-4 py-4 flex items-center justify-between">
                    <div className="label-tiny">{o.label.toUpperCase()}</div>
                    <CaretRight size={14} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" weight="bold" />
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* CTA banner */}
          <div
            data-testid="cta-banner"
            className="mt-14 card-lux rounded-xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8"
            style={{ borderColor: "var(--mg-gold-border)" }}
          >
            <div className="w-14 h-14 rounded-full flex items-center justify-center border shrink-0" style={{ borderColor: "var(--mg-gold)" }}>
              <Clapperboard size={26} weight="duotone" className="text-gold" />
            </div>
            <div className="flex-1">
              <div className="font-display text-2xl sm:text-3xl leading-tight">
                <span className="silver-gradient-text">Ready to create</span>{" "}
                <span className="gold-gradient-text">unforgettable memories?</span>
              </div>
              <p className="text-white/70 text-sm mt-2">Book your private theater experience today.</p>
            </div>
            <button
              data-testid="cta-book-btn"
              onClick={() => navigate("/book")}
              className="btn-gold rounded-md px-6 py-3.5 text-sm inline-flex items-center gap-2 shrink-0"
            >
              Book Now <CaretRight size={14} weight="bold" />
            </button>
          </div>

          {/* Feature bar */}
          <div
            data-testid="features-bar"
            className="mt-6 card-lux rounded-xl px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {FEATURES_BAR.map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <f.icon size={22} weight="duotone" className="text-gold shrink-0" />
                <div className="label-tiny">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MOONGLOW */}
      <section className="py-20 md:py-28 border-t border-mg bg-mg-2">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="label-tiny">The MoonGlow Experience</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
              <span className="silver-gradient-text">Every detail,</span> <br />
              <span className="gold-gradient-text">quietly perfect.</span>
            </h2>
            <p className="mt-6 text-white/70 text-base leading-relaxed max-w-lg">
              You walk in. The lights dim. The screen glows warm gold. Whatever you're celebrating, it looks and sounds like a scene from your favorite movie — because we crafted it that way.
            </p>
            <div className="mt-8 divider-lux" />
            <div className="mt-8">
              <div className="label-tiny mb-4">The Hall</div>
              <ul className="text-white/70 text-sm space-y-2">
                <li>· 4 / 6 / 10 members configurations</li>
                <li>· 2, 4 or 6 hour private sessions</li>
                <li>· 4K laser projection · Dolby 7.1 surround</li>
                <li>· 1/2 kg cake + welcome drink included with every booking</li>
                <li>· Full food & beverage menu — chef-curated</li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:gap-6">
            {FEATURES.map((f) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card-lux rounded-lg p-5"
              >
                <f.icon size={26} weight="duotone" className="text-gold" />
                <div className="font-display text-lg mt-3">{f.title}</div>
                <div className="text-sm text-white/60 mt-1 leading-relaxed">{f.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MENU PREVIEW */}
      <section id="menu" data-testid="menu-preview" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="label-tiny">Food & Beverages</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight max-w-xl">
                <span className="silver-gradient-text">A chef-curated menu,</span>{" "}
                <span className="gold-gradient-text">delivered to your seat.</span>
              </h2>
            </div>
            <div className="max-w-md text-white/70 text-sm leading-relaxed">
              From wood-fired pizzas to fresh mocktails, add exactly what you love — priced per item, no minimums, no fuss.
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mt-14">
            <div className="card-lux rounded-lg p-8">
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl">Kitchen</div>
                <div className="label-tiny text-gold">{food.length} items</div>
              </div>
              <div className="mt-5 divider-lux" />
              <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {food.slice(0, 8).map((f) => (
                  <div key={f.id} className="flex justify-between items-baseline border-b border-mg/60 pb-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2.5 h-2.5 rounded-sm border ${f.is_veg ? "border-green-500" : "border-red-500"} relative`}>
                        <span className={`absolute inset-0 m-auto w-1.5 h-1.5 rounded-full ${f.is_veg ? "bg-green-500" : "bg-red-500"}`} style={{ margin: "auto", position: "absolute", top: 0, bottom: 0, left: 0, right: 0 }} />
                      </span>
                      <span className="text-white/85">{f.name}</span>
                    </div>
                    <span className="text-gold">₹{f.price}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate("/book")}
                data-testid="menu-food-book-btn"
                className="btn-ghost-gold rounded-md px-4 py-2 mt-6 text-xs inline-flex items-center gap-2"
              >
                Add food while booking <CaretRight size={12} weight="bold" />
              </button>
            </div>

            <div className="card-lux rounded-lg p-8">
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl">Bar</div>
                <div className="label-tiny text-gold">{beverages.length} items</div>
              </div>
              <div className="mt-5 divider-lux" />
              <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {beverages.slice(0, 8).map((b) => (
                  <div key={b.id} className="flex justify-between items-baseline border-b border-mg/60 pb-2">
                    <span className="text-white/85">{b.name}</span>
                    <span className="text-gold">₹{b.price}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 label-tiny text-white/60">
                Occasion welcome drink is on the house · 1/2 kg cake included
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" data-testid="gallery" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="label-tiny">Inside MoonGlow</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
                <span className="gold-gradient-text">A cinematic sanctuary.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-14">
            <div className="md:col-span-2 aspect-[16/10] rounded-lg overflow-hidden card-lux">
              <ThreeDImage src={SCREEN} alt="Cinema screen" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[16/10] md:aspect-auto rounded-lg overflow-hidden card-lux">
              <ThreeDImage src={SEATS} alt="Theater seats" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[16/10] rounded-lg overflow-hidden card-lux">
              <ThreeDImage src={aiOr("occasion_birthday", IMG.birthday)} alt="Birthday theme" className="w-full h-full object-cover" />
            </div>
            <div className="md:col-span-2 aspect-[16/10] rounded-lg overflow-hidden card-lux">
              <ThreeDImage src={aiOr("occasion_proposal", IMG.proposal)} alt="Proposal theme" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="py-20 md:py-28 border-t border-mg bg-mg-2">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="label-tiny">Decoration Packages</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
                <span className="silver-gradient-text">Pick a theme,</span> <span className="gold-gradient-text">we do the rest.</span>
              </h2>
            </div>
            <button
              data-testid="packages-book-btn"
              onClick={() => navigate("/book")}
              className="btn-ghost-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2"
            >
              Start Booking <CaretRight size={14} weight="bold" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-14">
            {packages.slice(0, 6).map((p) => (
              <div key={p.id} data-testid={`package-${p.id}`} className="card-lux rounded-lg overflow-hidden flex flex-col">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image_url || IMG.hero} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="font-display text-xl">{p.name}</div>
                  <p className="text-sm text-white/60 mt-2 flex-1 leading-relaxed">{p.description}</p>
                  <div className="mt-5 flex items-baseline justify-between">
                    <div>
                      <div className="label-tiny">From</div>
                      <div className="font-display text-2xl gold-gradient-text">₹{p.price.toLocaleString("en-IN")}</div>
                    </div>
                    <Sparkle size={22} weight="duotone" className="text-gold" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="label-tiny">Guest Book</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight max-w-3xl">
            <span className="silver-gradient-text">Real celebrations,</span> <span className="gold-gradient-text">unforgettable nights.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-14">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="card-lux rounded-lg p-8">
                <div className="flex gap-1 text-gold">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} size={14} weight="fill" />
                  ))}
                </div>
                <p className="mt-5 text-white/85 leading-relaxed italic">"{t.quote}"</p>
                <div className="mt-6 divider-lux" />
                <div className="mt-6 flex items-center justify-between">
                  <div className="font-display text-lg">{t.name}</div>
                  <div className="label-tiny">{t.occasion}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" data-testid="contact-section" className="py-24 md:py-32 border-t border-mg bg-mg-2">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="label-tiny">Talk to Us</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
              <span className="gold-gradient-text">Have a special ask?</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Custom themes, corporate screenings, movie premieres for kids — tell us your dream and we'll design the night around it.
            </p>
            <div className="mt-10 divider-lux" />
            <div className="mt-8 grid gap-4 text-sm text-white/70">
              <div className="flex items-center gap-3"><Popcorn size={18} className="text-gold" weight="duotone" /> Snack + Cake bundles available</div>
              <div className="flex items-center gap-3"><Confetti size={18} className="text-gold" weight="duotone" /> On-site decor styling included</div>
              <div className="flex items-center gap-3"><FilmSlate size={18} className="text-gold" weight="duotone" /> Bring your own movie / playlist / video montage</div>
            </div>
          </div>

          <form onSubmit={submitContact} data-testid="contact-form" className="card-lux rounded-lg p-8 space-y-5">
            <div>
              <label className="label-tiny">Your Name</label>
              <input
                data-testid="contact-name"
                className="field mt-2 w-full rounded-md px-4 py-3"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                placeholder="Ananya Sharma"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-tiny">Phone</label>
                <input
                  data-testid="contact-phone"
                  className="field mt-2 w-full rounded-md px-4 py-3"
                  value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                  placeholder="+91 …"
                />
              </div>
              <div>
                <label className="label-tiny">Email</label>
                <input
                  data-testid="contact-email"
                  className="field mt-2 w-full rounded-md px-4 py-3"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  placeholder="you@email.com"
                />
              </div>
            </div>
            <div>
              <label className="label-tiny">Your Message</label>
              <textarea
                data-testid="contact-message"
                className="field mt-2 w-full rounded-md px-4 py-3 min-h-[120px]"
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                placeholder="Tell us about your celebration…"
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={sending}
              className="btn-gold rounded-md px-6 py-3 text-sm inline-flex items-center gap-2"
            >
              {sending ? "Sending…" : "Send Message"} <CaretRight size={14} weight="bold" />
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
